'use client';

/**
 * ShareButton — a viral hook shown after a catch or a boss victory.
 *
 * Builds a shareable link (https://surggies.com/?ref=share&card=<card-url>)
 * whose `card` points at /api/share/card — a gold-themed share image with the
 * Surggie sprite + branding. Clicking copies the link to the clipboard and
 * opens the rendered card in a new tab so the player can save/post it.
 *
 * Style: reuses the global `.btn-gold` pill (cream/ink/gold, no neon).
 * Props-driven; parents decide when to mount it.
 */

import { useState } from 'react';

type Props = {
  /** "catch" → "I caught X!"  ·  "victory" → "I beat the X!" */
  kind: 'catch' | 'victory';
  /** Surggie or boss name. */
  name: string;
  /** Optional level shown on the card. */
  level?: number;
};

function buildCardPath({ kind, name, level }: Props): string {
  const qs = new URLSearchParams({ kind, name });
  if (level != null) qs.set('level', String(level));
  return `/api/share/card?${qs.toString()}`;
}

function buildShareUrl(cardPath: string): string {
  // Absolute card URL so the link previews correctly off-site.
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://surggies.com';
  const card = encodeURIComponent(`${origin}${cardPath}`);
  return `https://surggies.com/?ref=share&card=${card}`;
}

export function ShareButton({ kind, name, level }: Props) {
  const [copied, setCopied] = useState(false);

  const cardPath = buildCardPath({ kind, name, level });
  const shareUrl = buildShareUrl(cardPath);

  const handleShare = async () => {
    // Open the rendered card so the player can see/save the image.
    if (typeof window !== 'undefined') window.open(cardPath, '_blank', 'noopener');

    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      // Prefer the native share sheet on mobile; fall back to clipboard copy.
      if (nav?.share) {
        await nav.share({
          title: kind === 'victory' ? `I beat the ${name}!` : `I caught ${name}!`,
          url: shareUrl,
        });
      } else if (nav?.clipboard) {
        await nav.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // User dismissed the share sheet, or clipboard was blocked — non-fatal.
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="btn-gold px-5 py-2"
      aria-label={copied ? 'Share link copied' : 'Share this moment'}
    >
      <span aria-hidden="true">{copied ? '✓' : '↗'}</span>
      <span className="font-mono font-bold uppercase tracking-wider">
        {copied ? 'Link copied!' : 'Share'}
      </span>
    </button>
  );
}
